
/**
 * Prefer AppSelect over raw Tamagui/MUI Select for new dropdowns.
 * Visual language matches Calendar Settings → Select Calendar.
 *
 * Web menus render through DropdownPortal to escape parent overflow and
 * stacking contexts. Native menus continue to use RNModal.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Platform,
  Pressable,
  Text,
  TextInput,
  View,
  ViewStyle,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { YStack } from 'tamagui';
import { FontAwesome } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { responsiveFontSize } from '@utils/responsiveFontSize';
import { responsivePadding } from '@stylesheet';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from '@components/Global/Spinner';
import { Modal } from '@utils/RNModal';
import DropdownPortal from '@utils/DropdownPortal';
import {
  decodeSelectValue,
  encodeSelectValue,
  getSelectMenuPaperStyle,
  getSelectPalette,
  getSelectTriggerStyle,
  getSelectValueStyle,
  getSelectWrapStyle,
} from './selectTokens';

export type AppSelectOption = {
  label: string;
  value: string;
};

export type AppSelectProps = {
  value: string;
  onValueChange: (value: string) => void;
  options: AppSelectOption[];
  placeholder?: string;
  groupLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  width?: number | string;
  flex?: number;
  hasError?: boolean;
  size?: 'default' | 'compact';
  style?: ViewStyle;
  minWidth?: number;
  searchable?: boolean;
  searchPlaceholder?: string;
  openDirection?: 'up' | 'down';
};

type MenuAnchor = {
  x: number;
  y: number;
  width: number;
  height: number;
};

const AppSelect = ({
  value,
  onValueChange,
  options,
  placeholder = 'Select',
  groupLabel,
  disabled = false,
  loading = false,
  width,
  flex,
  hasError = false,
  size = 'default',
  style,
  minWidth,
  searchable = false,
  searchPlaceholder,
  openDirection = 'down',
}: AppSelectProps) => {
  const { t } = useTranslation();
  const theme = themeSelector();
  const palette = useMemo(() => getSelectPalette(Boolean(theme)), [theme]);
  const compact = size === 'compact';
  const encodedValue = encodeSelectValue(value);
  const fontSize = responsiveFontSize(compact ? 13 : 16);

  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<MenuAnchor | null>(null);
  const [search, setSearch] = useState('');
  const wrapRef = useRef<View>(null);

  const isWeb = Platform.OS === 'web';

  const wrapStyle = useMemo(
    () => ({
      ...getSelectWrapStyle(palette, { hasError, compact, minWidth, open }),
      ...(width != null ? { width: width as ViewStyle['width'] } : null),
      ...(flex != null ? { flex } : null),
      ...style,
      zIndex: 1000000000,
    }),
    [palette, hasError, compact, minWidth, width, flex, style, open],
  );

  const triggerStyle = useMemo(
    () => getSelectTriggerStyle(palette, { compact }),
    [palette, compact],
  );

  const valueStyle = useMemo(
    () =>
      getSelectValueStyle(palette, {
        hasValue: Boolean(value),
        fontSize,
      }),
    [palette, value, fontSize],
  );

  const menuStyle = useMemo(() => getSelectMenuPaperStyle(palette), [palette]);

  const selectedLabel =
    options.find((option) => encodeSelectValue(option.value) === encodedValue)?.label ?? '';

  const filteredOptions = useMemo(() => {
    const available = options.filter((option) => option.value != null);
    const query = search.trim().toLowerCase();

    if (!searchable || !query) return available;

    return available.filter((option) => option.label.toLowerCase().includes(query));
  }, [options, search, searchable]);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setAnchor(null);
    setSearch('');
  }, []);

  const openMenu = useCallback(() => {
    if (disabled || loading) return;

    const node = wrapRef.current;

    if (!node || typeof node.measureInWindow !== 'function') {
      return;
    }

    node.measureInWindow((x, y, measuredWidth, measuredHeight) => {
      if (!measuredWidth || !measuredHeight) return;

      setAnchor({
        x,
        y,
        width: measuredWidth,
        height: measuredHeight,
      });

      setOpen(true);
    });
  }, [disabled, loading]);

  const menuWidth = Math.max(
    anchor?.width ?? (typeof width === 'number' ? width : 160),
    minWidth ?? 0,
    compact ? 120 : 160,
  );

  const rowHeight = compact ? 40 : 48;

  const estimatedMenuHeight = Math.min(
    300,
    12 +
    (searchable ? 56 : 0) +
    filteredOptions.length * rowHeight +
    (groupLabel ? 28 : 0),
  );

  const menuTop = anchor
    ? openDirection === 'up'
      ? Math.max(8, anchor.y - estimatedMenuHeight - 4)
      : anchor.y + anchor.height + 4
    : 0;

  const menuLeft = anchor ? Math.max(8, anchor.x) : 0;

  // On web, measureInWindow coordinates align with fixed-position elements.
  // On native, retain the original absolute positioning inside RNModal.
  const menuPositionStyle = isWeb
    ? ({ position: 'fixed' } as ViewStyle)
    : ({ position: 'absolute' } as ViewStyle);

  const menuRootStyle = isWeb
    ? ({
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 2147483647,
    } as ViewStyle)
    : styles.menuRoot;

  const menuContent = (
    <View pointerEvents={isWeb ? "box-none" : "auto"} style={menuRootStyle}>
      <Pressable
        style={[StyleSheet.absoluteFill, { zIndex: 0 }]}
        onPress={closeMenu}
        accessibilityRole="button"
        accessibilityLabel={t('Close')}
      />

      <View
        pointerEvents='auto'
        style={[
          menuStyle,
          menuPositionStyle,
          {
            top: menuTop,
            left: menuLeft,
            width: menuWidth,
            maxHeight: 300,
            zIndex: 2147483647,
          },
        ]}
      >
        {searchable ? (
          <View
            style={{
              padding: responsivePadding(12),
              borderBottomWidth: 1,
              borderBottomColor: palette.divider,
            }}
          >
            <TextInput
              value={search}
              onChangeText={setSearch}
              placeholder={searchPlaceholder || t('Search')}
              placeholderTextColor={palette.placeholder}
              style={{
                backgroundColor: theme ? '#f8fafc' : '#0f172a',
                borderWidth: 1,
                borderColor: palette.inputBorder,
                borderRadius: 8,
                paddingHorizontal: responsivePadding(12),
                paddingVertical: responsivePadding(8),
                fontSize: responsiveFontSize(14),
                color: palette.textPrimary,
              }}
            />
          </View>
        ) : null}

        {loading ? (
          <YStack padding="$4" alignItems="center" justifyContent="center">
            <LoadingIndicator color={palette.textSecondary} />
          </YStack>
        ) : (
            <ScrollView
              keyboardShouldPersistTaps="handled"
              nestedScrollEnabled
              style={{ maxHeight: 250 }}
            >
              {groupLabel ? (
                <Text
                  style={{
                    fontSize: responsiveFontSize(12),
                    fontWeight: '700',
                    color: palette.textSecondary,
                    paddingHorizontal: responsivePadding(16),
                    paddingTop: responsivePadding(10),
                    paddingBottom: responsivePadding(4),
                  }}
                >
                  {groupLabel}
                </Text>
              ) : null}

              {filteredOptions.length === 0 ? (
                <Text
                  style={{
                    color: palette.textSecondary,
                    fontSize: responsiveFontSize(14),
                    textAlign: 'center',
                    padding: responsivePadding(16),
                  }}
                >
                  {search ? t('noMatches') : placeholder}
                </Text>
              ) : (
                filteredOptions.map((option, index) => {
                  const itemValue = encodeSelectValue(option.value);
                  const selected = encodedValue === itemValue;

                  return (
                    <Pressable
                      key={`${itemValue}-${index}`}
                      onPress={() => {
                        onValueChange(decodeSelectValue(itemValue));
                        closeMenu();
                      }}
                      style={{
                        minHeight: rowHeight,
                        paddingHorizontal: responsivePadding(16),
                        paddingVertical: responsivePadding(12),
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: selected ? palette.selectedBg : 'transparent',
                        borderBottomWidth: index < filteredOptions.length - 1 ? 1 : 0,
                        borderBottomColor: palette.divider,
                        // @ts-expect-error web cursor
                        cursor: 'pointer',
                      }}
                    >
                      <Text
                        numberOfLines={2}
                        style={{
                          fontWeight: selected ? '600' : '400',
                          fontSize,
                          color: selected ? palette.selectedText : palette.menuText,
                          flex: 1,
                          flexShrink: 1,
                          marginRight: 8,
                        }}
                      >
                        {option.label}
                      </Text>

                      {selected ? (
                        <FontAwesome name="check" size={14} color={palette.check} />
                      ) : null}
                    </Pressable>
                  );
                })
            )}
          </ScrollView>
        )}
      </View>
    </View>
  );

  return (
    <View ref={wrapRef} style={wrapStyle} collapsable={false}>
      <Pressable
        disabled={disabled || loading}
        hitSlop={12}
        onPress={() => {
          if (open) closeMenu();
          else openMenu();
        }}
        style={[
          triggerStyle,
          {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            opacity: disabled || loading ? 0.55 : 1,
            // @ts-expect-error web cursor
            cursor: disabled || loading ? 'not-allowed' : 'pointer',
          },
        ]}
      >
        <Text numberOfLines={1} style={[valueStyle, { flex: 1, marginRight: 8 }]}>
          {selectedLabel || placeholder}
        </Text>

        {loading ? (
          <LoadingIndicator color={palette.textSecondary} />
        ) : (
          <FontAwesome
            name={open ? 'chevron-up' : 'chevron-down'}
            size={compact ? 12 : 14}
            color={palette.textSecondary}
          />
        )}
      </Pressable>

      {open &&
        (isWeb ? (
          <DropdownPortal>{menuContent}</DropdownPortal>
        ) : (
          <Modal
            visible
            transparent
            animationType="fade"
            onRequestClose={closeMenu}
          >
            {menuContent}
          </Modal>
        ))}
    </View>
  );
};

const styles = StyleSheet.create({
  menuRoot: {
    flex: 1,
  },
});

export default AppSelect;
